import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Email as EmailIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { userManagementService } from '../../services/userManagementService';
import type { UserAccessLevel } from '../../services/authService';

interface ClientUser {
  id: string;
  email: string;
  username: string;
  access_level: string;
  verified: boolean;
  status?: boolean;
  created: string;
  updated: string;
}

interface ManageUsersProps {
  currentUserAccessLevel?: UserAccessLevel;
  currentUserId?: string;
}

const ManageUsers: React.FC<ManageUsersProps> = ({ currentUserAccessLevel, currentUserId }) => {
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [auditWarning, setAuditWarning] = useState<string | null>(null);
  const [sendingEmails, setSendingEmails] = useState<Set<string>>(new Set());
  const [updatingRoles, setUpdatingRoles] = useState<Set<string>>(new Set());
  const [updatingStatus, setUpdatingStatus] = useState<Set<string>>(new Set());
  const [updatingEmail, setUpdatingEmail] = useState<Set<string>>(new Set());
  const [editingEmailUserId, setEditingEmailUserId] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 50;

  // Available roles that can be edited (only all_access can edit)
  const editableRoles: Array<'all_access' | 'support' | 'admin'> = ['admin', 'support', 'all_access'];
  
  // Check if current user can edit roles
  const canEditRoles = currentUserAccessLevel === 'all_access';
  const canEditStatus = currentUserAccessLevel === 'all_access';
  const canEditEmail = currentUserAccessLevel === 'all_access';

  const loadUsers = async (currentPage: number = 1, search: string = '') => {
    try {
      setLoading(true);
      setError(null);
      const result = await userManagementService.fetchExistingUsers(currentPage, perPage, search);
      setUsers(result.items);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(page, searchTerm);
  }, [page, searchTerm]);

  const handleSearch = () => {
    setPage(1); // Reset to first page when searching
    loadUsers(1, searchTerm);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleResendEmail = async (userEmail: string, userId: string) => {
    try {
      setSendingEmails((prev) => new Set(prev).add(userId));
      setError(null);
      setSuccess(null);
      
      await userManagementService.requestPasswordReset(userEmail);
      
      setSuccess(`Password reset email sent to ${userEmail}`);
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error sending email:', err);
      setError(err.message || 'Failed to send password reset email');
      // Clear error message after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setSendingEmails((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleRefresh = () => {
    loadUsers(page, searchTerm);
  };

  const handleRoleChange = async (userId: string, newAccessLevel: 'all_access' | 'support' | 'admin') => {
    try {
      setUpdatingRoles((prev) => new Set(prev).add(userId));
      setError(null);
      setSuccess(null);
      
      await userManagementService.updateUserAccessLevel(userId, newAccessLevel);
      
      // Update the user in the local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, access_level: newAccessLevel } : user
        )
      );
      
      setSuccess(`User role updated successfully to ${newAccessLevel}`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error updating user role:', err);
      setError(err.message || 'Failed to update user role');
      setTimeout(() => setError(null), 5000);
    } finally {
      setUpdatingRoles((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleStatusChange = async (userId: string, newStatus: boolean) => {
    try {
      setUpdatingStatus((prev) => new Set(prev).add(userId));
      setError(null);
      setSuccess(null);

      await userManagementService.updateUserStatus(userId, newStatus);

      setUsers((prevUsers) =>
        prevUsers.map((user) => (user.id === userId ? { ...user, status: newStatus } : user))
      );

      setSuccess(`User status updated to ${newStatus ? 'active' : 'inactive'}`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error updating user status:', err);
      setError(err.message || 'Failed to update user status');
      setTimeout(() => setError(null), 5000);
    } finally {
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const startEmailEdit = (user: ClientUser) => {
    setEditingEmailUserId(user.id);
    setEmailDraft(user.email || '');
  };

  const cancelEmailEdit = () => {
    setEditingEmailUserId(null);
    setEmailDraft('');
  };

  const handleEmailSave = async (user: ClientUser) => {
    const newEmail = emailDraft.trim();
    if (!newEmail) {
      setError('Email cannot be empty');
      return;
    }
    if (newEmail === user.email) {
      cancelEmailEdit();
      return;
    }

    try {
      setUpdatingEmail((prev) => new Set(prev).add(user.id));
      setError(null);
      setSuccess(null);
      setAuditWarning(null);

      const result = await userManagementService.updateUserEmail(user.id, newEmail, user.email);
      const updatedEmail = result?.record?.email || newEmail;

      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? { ...u, email: updatedEmail } : u))
      );
      setSuccess('User email updated successfully');
      setTimeout(() => setSuccess(null), 5000);

      cancelEmailEdit();

      try {
        await userManagementService.createAuditLog({
          collection: 'client_users',
          record_id: user.id,
          action: 'email_update',
          user_id: currentUserId || null,
          old_data: { email: user.email },
          new_data: { email: updatedEmail },
        });
      } catch (auditErr: any) {
        console.warn('Audit log write failed:', auditErr);
        setAuditWarning('Audit log failed to save');
        setTimeout(() => setAuditWarning(null), 5000);
      }
    } catch (err: any) {
      console.error('Error updating user email:', err);
      setError(err.message || 'Failed to update user email');
      setTimeout(() => setError(null), 5000);
    } finally {
      setUpdatingEmail((prev) => {
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Manage Client Users
        </Typography>
        <Tooltip title="Refresh list">
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {auditWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAuditWarning(null)}>
          {auditWarning}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search by email or username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          Search
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Username</strong></TableCell>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        {editingEmailUserId === user.id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              value={emailDraft}
                              onChange={(e) => setEmailDraft(e.target.value)}
                              size="small"
                              variant="outlined"
                              sx={{ minWidth: 220 }}
                              disabled={updatingEmail.has(user.id)}
                            />
                            <Tooltip title="Save email">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEmailSave(user)}
                                  disabled={updatingEmail.has(user.id)}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={cancelEmailEdit} disabled={updatingEmail.has(user.id)}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">{user.email}</Typography>
                            {canEditEmail && (
                              <Tooltip title="Edit email">
                                <IconButton
                                  size="small"
                                  onClick={() => startEmailEdit(user)}
                                  disabled={updatingEmail.has(user.id)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>
                        {canEditRoles && editableRoles.includes(user.access_level as 'all_access' | 'support' | 'admin') ? (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={user.access_level as 'all_access' | 'support' | 'admin'}
                              onChange={(e) =>
                                handleRoleChange(
                                  user.id,
                                  e.target.value as 'all_access' | 'support' | 'admin'
                                )
                              }
                              disabled={updatingRoles.has(user.id)}
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {editableRoles.map((role) => (
                                <MenuItem key={role} value={role}>
                                  {role}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={user.access_level || 'normal'}
                            color={
                              user.access_level === 'all_access'
                                ? 'primary'
                                : user.access_level === 'admin'
                                ? 'secondary'
                                : user.access_level === 'support'
                                ? 'info'
                                : 'default'
                            }
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const isActive = user.status !== false;
                          if (canEditStatus) {
                            return (
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={isActive}
                                    onChange={(e) => handleStatusChange(user.id, e.target.checked)}
                                    disabled={updatingStatus.has(user.id)}
                                    size="small"
                                  />
                                }
                                label={isActive ? 'Active' : 'Inactive'}
                              />
                            );
                          }
                          return (
                            <Chip
                              label={isActive ? 'Active' : 'Inactive'}
                              color={isActive ? 'success' : 'default'}
                              size="small"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>{formatDate(user.created)}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            sendingEmails.has(user.id) ? (
                              <CircularProgress size={16} />
                            ) : (
                              <EmailIcon />
                            )
                          }
                          onClick={() => handleResendEmail(user.email, user.id)}
                          disabled={sendingEmails.has(user.id)}
                        >
                          {sendingEmails.has(user.id) ? 'Sending...' : 'Resend Email'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Showing {users.length} of {totalItems} user{totalItems !== 1 ? 's' : ''}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ManageUsers;

